import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { TextSprite } from '../TextSprite';
import { v4 as uuid } from 'uuid';
import { Utils } from '../utils';
import { Viewer } from '../viewer/viewer';
import { DefaultColors } from '../defines';
import { SphereMarker } from './SphereMarker';
import { calculatePolygonArea2D, calculatePolygonArea3D, isPolygonPlanar } from './GeometryUtils';
import { createAreaLabel, createHeightLabel, createVolumeLabel } from './LabelUtils';
import type { ReactiveProperty, Subscription } from '@pointorama/potree-shared';
import { getAnnotationNameProperty, getAnnotationColorProperty, getAnnotationVisibleProperty } from '@pointorama/potree-shared';
import type { EditableShapeKind } from '../personas/tools/implementations/line-tool/EditableShape';

export enum MeasureTools {
  POINT = 'POINT',
  DISTANCE = 'DISTANCE',
  AREA = 'AREA',
}

/**
 * Ownership discriminator for editing: which tool family a shape belongs to.
 * Orthogonal to `MeasureTools` (geometry) — rooms/openings/surfaces all render
 * as AREA geometry but must only be editable by their own tools.
 */
export type AnnotationDomain = 'point' | 'distance' | 'area' | EditableShapeKind;

export type MeasureConstructorArgs = {
  visible?: boolean | null;
  color?: THREE.Color | null;
  finished?: boolean | null;
  viewer: Viewer;
  identifier?: string | null;
  selected?: boolean | null;
  isCADMeasure?: boolean | null;
  name?: string | null;
};

export type Point = {
  position: THREE.Vector3;
  color?: Uint8Array;
  classification?: Uint8Array;
  intensity?: Float32Array;
  pointCloudId?: string;
};

export enum Label {
  COORDINATE = 'COORDINATE',
  LENGTH = 'LENGTH',
  AREA = 'AREA',
  VOLUME = 'VOLUME',
}

export class Measure extends THREE.Object3D {
  points: Point[];
  private _showDistances?: boolean;
  private _showCoordinates: boolean;
  private _showArea: boolean;
  private _showVolume: boolean;
  private _showHeight: boolean;
  private _closed: boolean;
  maxMarkers: number;
  lineGeometry: LineGeometry;
  lineGeometrySize: number;
  lineMaterial: LineMaterial;
  backgroundLineMaterial!: LineMaterial;
  backgroundLineTouchMaterial!: LineMaterial;
  line: Line2;
  backgroundLine!: Line2;
  backgroundLineTouch!: Line2;
  color: THREE.Color;
  assignedColor: THREE.Color;
  selected: boolean;
  spheres: SphereMarker[];
  edgeLabels: TextSprite[] = [];
  coordinateLabels: TextSprite[] = [];
  areaLabel!: TextSprite;
  heightLabel!: TextSprite;
  volumeLabels: TextSprite[] = [];
  volumes: number[] = [];
  lengthUnit?: { unitspermeter: number } | null;
  lengthUnitDisplay?: { unitspermeter: number; code: string } | null;
  type: MeasureTools = MeasureTools.POINT;
  /** Edit ownership — only tools with a matching editableDomain may edit this shape. */
  domain: AnnotationDomain = 'point';
  identifier = '';
  groupName = '';
  groupIdentifier: string | null = null;
  finished = false;
  viewer: Viewer;
  needsUpdate = false;
  isCADMeasure = false;
  depthFadeEnabled = true;

  // Reactive properties for annotation name, color, visible
  private _nameProperty?: ReactiveProperty<string>;
  private _nameSubscription?: Subscription;
  private _colorProperty?: ReactiveProperty<string>;
  private _colorSubscription?: Subscription;
  private _visibleProperty?: ReactiveProperty<boolean>;
  private _visibleSubscription?: Subscription;

  constructor(args: MeasureConstructorArgs) {
    super();
    this.name = args.name || 'Measure';
    this.points = [];
    this._showDistances = true;
    this._showCoordinates = false;
    this._showArea = false;
    this._showVolume = false;
    this._showHeight = false;
    this._closed = true;
    this.maxMarkers = Number.MAX_SAFE_INTEGER;
    this.isCADMeasure = args.isCADMeasure || false;

    this.assignedColor = args.color || DefaultColors.OBJECTS_THREE;
    this.color = this.assignedColor;
    this.selected = args.selected || false;

    this.viewer = args.viewer;
    this.identifier = args.identifier || uuid();

    this.spheres = [];

    this.lineGeometry = new LineGeometry();
    this.lineGeometrySize = 0;
    this.lineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 1,
      resolution: new THREE.Vector2(1000, 1000),
      depthTest: false,
      depthWrite: false,
      transparent: true,
    });
    this.line = new Line2(this.lineGeometry, this.lineMaterial);
    this.add(this.line);

    if (args.visible != null) this.visible = args.visible;
    this.finished = args.finished || false;

    this.depthFadeEnabled = this.finished || false;

    if (!this.isCADMeasure) {
      this.backgroundLineMaterial = new LineMaterial({
        color: this.color,
        linewidth: 15,
        resolution: new THREE.Vector2(1000, 1000),
        transparent: true,
        depthTest: false,
        opacity: this.selected ? 0.25 : 0.0,
      });
      this.backgroundLineTouchMaterial = new LineMaterial({
        color: DefaultColors.OBJECTS,
        linewidth: 30,
        resolution: new THREE.Vector2(1000, 1000),
        transparent: true,
        opacity: 0.0,
        depthTest: false,
      });

      this.backgroundLine = new Line2(this.lineGeometry, this.backgroundLineMaterial);
      this.backgroundLine.name = 'MeasureBackgroundLine';
      this.backgroundLineTouch = new Line2(this.lineGeometry, this.backgroundLineTouchMaterial);
      this.add(this.backgroundLine);
      this.add(this.backgroundLineTouch);

      this.edgeLabels = [];
      this.coordinateLabels = [];

      this.areaLabel = createAreaLabel();
      this.heightLabel = createHeightLabel();
      this.volumes = [];
      this.volumeLabels = [];

      this.add(this.areaLabel);
      this.add(this.heightLabel);
    }

    this.loadReactiveProperties(args);
  }

  loadReactiveProperties(args: Partial<MeasureConstructorArgs> = {}): void {
    if (!this.identifier) return;

    try {
      this._nameSubscription?.unsubscribe();
      this._colorSubscription?.unsubscribe();
      this._visibleSubscription?.unsubscribe();

      // --- Name ---
      this._nameProperty = getAnnotationNameProperty(this.identifier);
      // Read the property value into the Potree object. For new annotations,
      // setValuesLocally (called by useCreateAnnotation) sets the unique name
      // before or after this runs. For existing annotations, preloadValues
      // already populated the property from the DB.
      // Never write this.name back to the property here — it's the raw base name
      // (e.g. "Point") without the unique suffix, and writing it would trigger a
      // debounced save that overwrites the correct name in the database.
      if (this._nameProperty.value) this.name = this._nameProperty.value;
      this._nameSubscription = this._nameProperty.subscribeToChanges((value) => {
        this.name = value;
      });

      // --- Color ---
      // Read the property value into the local Potree object. Don't write
      // args.color back here — for brand-new annotations the create flow
      // (useCreateAnnotation / annotationFactories) already persists the
      // initial color, and on load any args.color is just a sync-side hint
      // that should not overwrite what's already in the property store.
      this._colorProperty = getAnnotationColorProperty(this.identifier);
      this._applyColor(this._colorProperty.value);
      this._colorSubscription = this._colorProperty.subscribeToChanges((value) => {
        this._applyColor(value);
      });

      // --- Visible ---
      // Same reasoning as color: never write args.visible back from the
      // constructor — the property store is the source of truth.
      this._visibleProperty = getAnnotationVisibleProperty(this.identifier);
      this.visible = this._visibleProperty.value;
      this._visibleSubscription = this._visibleProperty.subscribeToChanges((value) => {
        this.visible = value;
      });
    } catch {
      // Global property service not initialized yet, will use default
    }
  }

  /**
   * Apply a color change from the reactive property to all visual elements.
   */
  private _applyColor(hexColor: string): void {
    const color = new THREE.Color(hexColor);
    this.assignedColor = color;
    this.color = color;
    if (this.lineMaterial) this.lineMaterial.color = color;
    if (this.backgroundLineMaterial) this.backgroundLineMaterial.color = color;
    for (const sphere of this.spheres) {
      sphere.setColor(color);
    }
    this.needsUpdate = true;
  }

  /**
   * Get annotation color as hex string from reactive property if available
   */
  get annotationColor(): string {
    return this._colorProperty?.value ?? '#' + this.assignedColor.getHexString();
  }

  /**
   * Set annotation color from hex string (updates reactive property + visuals)
   */
  set annotationColor(value: string) {
    this._applyColor(value);
    if (this._colorProperty) {
      this._colorProperty.value = value;
    }
  }

  /**
   * Get annotation visibility from reactive property if available
   */
  get annotationVisible(): boolean {
    return this._visibleProperty?.value ?? this.visible;
  }

  /**
   * Set annotation visibility (updates reactive property if available)
   */
  set annotationVisible(value: boolean) {
    this.visible = value;
    if (this._visibleProperty) {
      this._visibleProperty.value = value;
    }
  }

  addVolumeLabels(volumes: number[]) {
    for (const volumeIndex in volumes) {
      this.volumes.push(volumes[volumeIndex]);
      this.volumeLabels.push(createVolumeLabel());
      this.add(this.volumeLabels[volumeIndex]);
    }
  }

  finishMeasurement() {
    this.finished = true;
    this.depthFadeEnabled = true;
  }

  addEdgeLabel() {
    if (!this.edgeLabels || this.isCADMeasure) return;
    const edgeLabel = new TextSprite();
    edgeLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
    edgeLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 0.3 });
    edgeLabel.material.depthTest = false;
    edgeLabel.visible = false;
    edgeLabel.fontsize = 16;
    this.edgeLabels.push(edgeLabel);
    this.add(edgeLabel);
  }

  addCoordinateLabel() {
    if (!this.coordinateLabels || this.isCADMeasure) return;
    const coordinateLabel = new TextSprite();
    coordinateLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
    coordinateLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 0.3 });
    coordinateLabel.fontsize = 16;
    coordinateLabel.material.depthTest = false;
    coordinateLabel.material.opacity = 1;
    coordinateLabel.visible = false;
    this.coordinateLabels.push(coordinateLabel);
    this.add(coordinateLabel);
  }

  removeMarker(index: number) {
    if (this.isCADMeasure) return;
    this.points.splice(index, 1);

    this.remove(this.spheres[index]);

    const edgeIndex = index === 0 ? 0 : index - 1;

    this.remove(this.edgeLabels[edgeIndex]);
    this.edgeLabels.splice(edgeIndex, 1);
    this.coordinateLabels.splice(index, 1);

    this.spheres.splice(index, 1);

    this.needsUpdate = true;
  }

  getArea() {
    if (this.points.length < 3) return 0;

    const points = this.points.map((p) => p.position);

    if (this.isPlanar()) {
      return calculatePolygonArea3D(points);
    }

    return calculatePolygonArea2D(points);
  }

  isPlanar() {
    if (this.points.length < 4) return true;

    const points = this.points.map((p) => p.position);
    return isPolygonPlanar(points);
  }

  /**
   * Vertical extent of the measurement (max z − min z). Used by openings to
   * report the height of a door/window, whose corners sit on a vertical wall.
   */
  getHeight() {
    if (this.points.length < 2) return 0;

    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const point of this.points) {
      if (point.position.z < minZ) minZ = point.position.z;
      if (point.position.z > maxZ) maxZ = point.position.z;
    }

    return maxZ - minZ;
  }

  /** Convert a raw length (model units) to the display unit and format it with
   *  its unit suffix, e.g. "1.20 m". */
  protected formatLength(value: number): string {
    let v = value;
    let suffix = '';
    if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
      v = (v / this.lengthUnit.unitspermeter) * this.lengthUnitDisplay.unitspermeter;
      suffix = this.lengthUnitDisplay.code;
    }
    return `${Utils.addCommas(v.toFixed(2))} ${suffix}`;
  }

  /** Text shown in the height label. Defaults to the vertical extent; subclasses
   *  (e.g. openings) override to display additional values such as elevation. */
  protected getHeightLabelText(): string {
    return this.formatLength(this.getHeight());
  }

  /** Raw height value(s) in model units, for export. Defaults to the vertical
   *  extent; subclasses (e.g. openings) override to add the sill elevation. */
  getHeightValues(): number[] {
    return [this.getHeight()];
  }

  getTotalDistance() {
    if (this.points.length === 0) {
      return 0;
    }

    let distance = 0;

    for (let i = 1; i < this.points.length; i++) {
      const prev = this.points[i - 1].position;
      const curr = this.points[i].position;
      const d = prev.distanceTo(curr);

      distance += d;
    }

    if (this.closed && this.points.length > 1) {
      const first = this.points[0].position;
      const last = this.points[this.points.length - 1].position;
      const d = last.distanceTo(first);

      distance += d;
    }

    return distance;
  }

  select() {
    if (this.isCADMeasure) return;
    this.selected = true;
    if (this.type === MeasureTools.POINT) {
      this.spheres[0].showBorder();
    }

    this.backgroundLineMaterial.opacity = 0.25;
  }

  deselect() {
    if (this.isCADMeasure) return;
    this.selected = false;
    if (this.type === MeasureTools.POINT) {
      this.spheres[0].hideBorder();
    }

    this.backgroundLineMaterial.opacity = 0.0;
  }

  updateObjectPosition() {
    this.position.x = this.points[0].position.x;
    this.position.y = this.points[0].position.y;
    this.position.z = this.points[0].position.z;
  }

  updatePointPosition(index: number) {
    const position = this.points[index].position.clone().sub(this.points[0].position);
    const sphere = this.spheres[index];
    if (!sphere) return;
    sphere.position.copy(position);
  }

  updatePointLabelPosition(index: number) {
    if (this.isCADMeasure) return;
    const position = this.points[index].position;
    const coordinateLabel = this.coordinateLabels[index];

    if (coordinateLabel) {
      const transformedPosition = new THREE.Vector3();
      if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
        const factor = Math.pow(this.lengthUnit.unitspermeter, 1) * Math.pow(this.lengthUnitDisplay.unitspermeter, 1);
        transformedPosition.copy(position).multiplyScalar(factor);
      }
      const msg = transformedPosition
        .toArray()
        .map((p) => Utils.addCommas(p.toFixed(2)))
        .join(' / ');
      coordinateLabel.setText(msg);

      coordinateLabel.visible = this.showCoordinates;
    }
  }

  updateLinePositions() {
    const hasLine = this.points.length >= 2;
    this.line.visible = hasLine;
    if (this.backgroundLine) this.backgroundLine.visible = hasLine;
    if (this.backgroundLineTouch) this.backgroundLineTouch.visible = hasLine;
    if (!hasLine) return;

    if (this.points.length > this.lineGeometrySize) {
      this.lineGeometry.dispose();
      this.lineGeometry = new LineGeometry();
      this.line.geometry = this.lineGeometry;
      if (this.backgroundLine) this.backgroundLine.geometry = this.lineGeometry;
      if (this.backgroundLineTouch) this.backgroundLineTouch.geometry = this.lineGeometry;
    }

    const vertices = this.points.map((point) => point.position.clone().sub(this.points[0].position).toArray()).flat();
    if (this.type === MeasureTools.AREA) {
      vertices.push(0, 0, 0);
    }
    this.lineGeometry.setPositions(vertices);
  }

  /**
   * Update the position and text of an edge label for a specific line segment.
   * Uses relative coordinates to avoid floating-point precision issues with large world coordinates.
   */
  updateEdgeLabelPosition(index: number) {
    if (this.isCADMeasure) return;
    const edgeLabel = this.edgeLabels[index];

    const lastIndex = this.points.length - 1;
    const nextIndex = (index + 1) % (lastIndex + 1);

    const point = this.points[index];
    const nextPoint = this.points[nextIndex];
    const origin = this.points[0].position;

    // Calculate center using RELATIVE coordinates first to avoid precision issues
    const relativePoint = point.position.clone().sub(origin);
    const relativeNextPoint = nextPoint.position.clone().sub(origin);
    const center = relativePoint.add(relativeNextPoint).multiplyScalar(0.5);

    let distance = point.position.distanceTo(nextPoint.position);

    edgeLabel.position.copy(center);
    if (!this.finished && nextIndex === lastIndex) {
      const distX = point.position.x - nextPoint.position.x;
      const offset = new THREE.Vector3((10 * edgeLabel.scale.x * Math.sign(distX)) / (distance + 10), 0, 0);
      edgeLabel.position.add(offset);
    }

    let suffix = '';
    if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
      distance = (distance / this.lengthUnit.unitspermeter) * this.lengthUnitDisplay.unitspermeter; //convert to meters then to the display unit
      suffix = this.lengthUnitDisplay.code;
    }

    const txtLength = Utils.addCommas(distance.toFixed(2));
    edgeLabel.setText(`${txtLength} ${suffix}`);
    edgeLabel.visible = (this.showDistances && (index < lastIndex || this.closed) && this.points.length >= 2 && distance > 0) || false;
    if (this.type === MeasureTools.AREA && lastIndex < 2 && index === 1) {
      edgeLabel.visible = false;
    }
  }

  /**
   * Update the position and text of area and volume labels.
   * Uses relative coordinates to avoid floating-point precision issues with large world coordinates.
   */
  updateAreaAndVolumeLabelPosition() {
    if (this.isCADMeasure) return;

    // Calculate centroid using RELATIVE coordinates to avoid precision issues
    const origin = this.points[0].position;
    const centroid = new THREE.Vector3();
    for (let i = 0; i < this.points.length; i++) {
      // Add relative position (subtract origin first, then add)
      centroid.add(this.points[i].position.clone().sub(origin));
    }
    centroid.divideScalar(this.points.length);

    // Area label
    if (this.areaLabel) {
      this.areaLabel.position.copy(centroid);
      this.areaLabel.visible = this.showArea && this.points.length >= 3;
      let area = this.getArea();

      let suffix = '';
      if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
        area = (area / Math.pow(this.lengthUnit.unitspermeter, 2)) * Math.pow(this.lengthUnitDisplay.unitspermeter, 2); //convert to square meters then to the square display unit
        suffix = this.lengthUnitDisplay.code;
      }

      const txtArea = Utils.addCommas(area.toFixed(1));
      const msg = `${txtArea} ${suffix}\u00B2`;
      this.areaLabel.setText(msg);
    }

    // Height label - nudged below the centroid so it doesn't overlap the
    // area label when both are shown.
    if (this.heightLabel) {
      const height = this.getHeight();
      this.heightLabel.position.set(centroid.x, centroid.y, centroid.z - height * 0.2);
      this.heightLabel.visible = this.showHeight && this.points.length >= 2 && height > 0;
      this.heightLabel.setText(this.getHeightLabelText());
    }

    // Volume labels
    if (this.volumes) {
      for (const [index, value] of this.volumes.entries()) {
        let labelPositionOffset;
        if (this.areaLabel?.visible) {
          labelPositionOffset = (centroid.length() / (this.volumes.length * 3)) * (index + 1);
        } else {
          labelPositionOffset = (centroid.length() / (this.volumes.length * 3)) * index;
        }
        const position = new THREE.Vector3(centroid.x, centroid.y - labelPositionOffset, centroid.z);
        this.volumeLabels[index].position.copy(position);
        this.volumeLabels[index].visible = this.showVolume;
        let volume = value;
        let suffix = '';
        if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
          volume = (volume / Math.pow(this.lengthUnit.unitspermeter, 3)) * Math.pow(this.lengthUnitDisplay.unitspermeter, 3); //convert to square meters then to the square display unit
          suffix = this.lengthUnitDisplay.code;
        }

        const txtVolume = Utils.addCommas(volume.toFixed(1));
        const msg = `${txtVolume} ${suffix}\u00B3`;
        this.volumeLabels[index].setText(msg);
      }
    }
  }

  updatePositions() {
    // virtual
  }

  /** Virtual. Subclasses override to rescale labels and line resolutions
   *  for the given camera + canvas. Defaults to the main view's active
   *  camera + main renderer size when args are omitted. */
  updateScales(_cameraOverride?: THREE.PerspectiveCamera | THREE.OrthographicCamera, _widthOverride?: number, _heightOverride?: number) {
    // virtual
  }

  update(cameraOverride?: THREE.PerspectiveCamera | THREE.OrthographicCamera, widthOverride?: number, heightOverride?: number) {
    if (this.needsUpdate) {
      this.updatePositions();
      this.needsUpdate = false;
    }
    this.updateScales(cameraOverride, widthOverride, heightOverride);
    // Sphere-marker rescaling is centralized in `Viewer.rescaleLabelsForView`
    // — runs once per render pass for all sphere owners (measurements,
    // rooms, wallSketches), so we don't duplicate the loop here.
  }

  get showCoordinates() {
    return this._showCoordinates;
  }

  set showCoordinates(value) {
    if (this._showCoordinates !== value) {
      this._showCoordinates = value;
      this.needsUpdate = true;
    }
  }

  get showArea() {
    return this._showArea;
  }

  set showArea(value) {
    if (this._showArea !== value) {
      this._showArea = value;
      this.needsUpdate = true;
    }
  }

  get showVolume() {
    return this._showVolume;
  }

  set showVolume(value) {
    if (this._showVolume !== value) {
      this._showVolume = value;
      this.needsUpdate = true;
    }
  }

  get showHeight() {
    return this._showHeight;
  }

  set showHeight(value) {
    if (this._showHeight !== value) {
      this._showHeight = value;
      this.needsUpdate = true;
    }
  }

  get closed() {
    return this._closed;
  }

  set closed(value) {
    if (this._closed !== value) {
      this._closed = value;
      this.needsUpdate = true;
    }
  }

  get showDistances() {
    return this._showDistances;
  }

  set showDistances(value) {
    if (this._showDistances !== value) {
      this._showDistances = value;
      this.needsUpdate = true;
    }
  }

  dispose(): void {
    this._nameSubscription?.unsubscribe();
    this._colorSubscription?.unsubscribe();
    this._visibleSubscription?.unsubscribe();
  }
}
